import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ArrowLeft, MapPin, Zap, Shield, TrendingUp, DollarSign, Navigation, Loader2, Info, Truck } from 'lucide-react';
import { useLaadpleinLocatieDetail } from '@/hooks/useLaadpleinLocatieDetail';
import { UnifiedMapComponent } from '@/hooks/useMapWithProvider';
import type { ScoreBreakdown } from '@/lib/laadplein-scoring';
import { useState } from 'react';

const STATUS_COLORS: Record<string, string> = {
  prospect: 'bg-blue-100 text-blue-800',
  onderzoek: 'bg-amber-100 text-amber-800',
  haalbaar: 'bg-green-100 text-green-800',
  go: 'bg-emerald-100 text-emerald-800',
  'no-go': 'bg-red-100 text-red-800',
  gearchiveerd: 'bg-gray-100 text-gray-800',
};

function ScoreBar({ breakdown, icon }: { breakdown: ScoreBreakdown; icon: React.ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="space-y-1">
            <div className="flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-gray-600">
                {icon}
                {breakdown.label}
              </span>
              <span className="font-bold text-emerald-700">{breakdown.score}</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all"
                style={{ width: `${breakdown.score * 10}%` }}
              />
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="max-w-xs">
          <div className="space-y-1.5">
            {breakdown.factors.map((f, i) => (
              <div key={i} className="text-xs">
                <div className="flex justify-between gap-3">
                  <span className="font-medium">{f.label}: {f.value}</span>
                  <span className={f.impact >= 0 ? 'text-green-600' : 'text-red-500'}>
                    {f.impact > 0 ? '+' : ''}{f.impact}
                  </span>
                </div>
                <div className="text-muted-foreground">{f.tooltip}</div>
              </div>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function LaadpleinLocatieDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overzicht');

  const {
    locatie,
    concurrenten,
    verkeersdata,
    nearestCompetitor,
    nearestCorridor,
    autoScore,
    truckHpc25km,
    concurrenten10km,
    isLoading,
  } = useLaadpleinLocatieDetail(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!locatie) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Locatie niet gevonden</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/crm/laadplein')}>
          Terug naar overzicht
        </Button>
      </div>
    );
  }

  // Map sites for the competitor map
  const mapSites = [
    // The location itself
    {
      id: locatie.id,
      name: locatie.naam,
      latitude: locatie.latitude,
      longitude: locatie.longitude,
      isTarget: true,
    },
    // Competitors
    ...concurrenten.map((c: any) => ({
      id: c.id || c.ocm_id,
      name: c.naam,
      latitude: c.latitude,
      longitude: c.longitude,
      max_vermogen_kw: c.max_vermogen_kw,
      aantal_truck_laders: c.aantal_truck_laders,
      afstand_km: c.afstand_km,
    })),
  ].filter(s => s.latitude && s.longitude);

  const getMarkerColor = (site: any) => {
    if (site.isTarget) return '#059669'; // emerald-600 for the location
    const kw = site.max_vermogen_kw ?? 0;
    if (kw >= 350) return '#7f1d1d';
    if (kw >= 150) return '#dc2626';
    if (kw >= 50) return '#f97316';
    return '#3b82f6';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/crm/laadplein')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold">{locatie.naam}</h1>
            <Badge className={STATUS_COLORS[locatie.status] ?? 'bg-gray-100 text-gray-800'}>
              {locatie.status ?? 'prospect'}
            </Badge>
            {locatie.spula_eligible && (
              <Badge className="bg-emerald-100 text-emerald-800">SPULA eligible</Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            {[locatie.adres, locatie.postcode, locatie.plaats].filter(Boolean).join(' · ')}
          </p>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Totaalscore</div>
            <div className="text-2xl font-bold text-emerald-600">
              {autoScore ? autoScore.totaal.toFixed(1) : (locatie.totaal_score?.toFixed(1) ?? '-')}
            </div>
            <div className="text-xs text-muted-foreground">/10 (auto-berekend)</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Dichtstbijzijnde concurrent</div>
            <div className="text-lg font-semibold">
              {nearestCompetitor ? `${nearestCompetitor.afstand_km} km` : '-'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {nearestCompetitor?.naam ?? 'Geen data'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Dichtstbijzijnde corridor</div>
            <div className="text-lg font-semibold">
              {nearestCorridor ? `${nearestCorridor.afstand_km} km` : '-'}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {nearestCorridor ? `${nearestCorridor.weg} ${nearestCorridor.traject}` : 'Geen data'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3">
            <div className="text-xs text-muted-foreground">Truck HPC in 25km</div>
            <div className="text-lg font-semibold">{truckHpc25km}</div>
            <div className="text-xs text-muted-foreground">
              {concurrenten10km} totaal in 10km
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overzicht">Overzicht</TabsTrigger>
          <TabsTrigger value="kaart">Kaart</TabsTrigger>
          <TabsTrigger value="scores">Scores</TabsTrigger>
          <TabsTrigger value="concurrenten">Concurrenten ({concurrenten.length})</TabsTrigger>
          <TabsTrigger value="verkeer">Verkeer</TabsTrigger>
        </TabsList>

        {/* Overzicht */}
        <TabsContent value="overzicht">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Locatie-eigenschappen</CardTitle></CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">Oppervlakte</dt>
                  <dd>{locatie.oppervlakte_m2?.toLocaleString() ?? '-'} m²</dd>
                  <dt className="text-muted-foreground">Netaansluiting</dt>
                  <dd>{locatie.netaansluiting_kw ?? '-'} kW</dd>
                  <dt className="text-muted-foreground">Netbeheerder</dt>
                  <dd>{locatie.netbeheerder ?? '-'}</dd>
                  <dt className="text-muted-foreground">Netcongestie</dt>
                  <dd>
                    <Badge variant="outline" className={
                      locatie.netcongestie_status === 'zwaar' ? 'border-red-300 text-red-700' :
                      locatie.netcongestie_status === 'licht' ? 'border-amber-300 text-amber-700' :
                      'border-green-300 text-green-700'
                    }>
                      {locatie.netcongestie_status ?? 'onbekend'}
                    </Badge>
                  </dd>
                  <dt className="text-muted-foreground">Afstand A/N-weg</dt>
                  <dd>{locatie.afstand_a_n_weg_km ?? '-'} km</dd>
                  <dt className="text-muted-foreground">Bedrijventerrein</dt>
                  <dd>{locatie.op_bedrijventerrein ? 'Ja' : 'Nee'}</dd>
                  <dt className="text-muted-foreground">Huidig gebruik</dt>
                  <dd>{locatie.huidig_gebruik ?? '-'}</dd>
                  <dt className="text-muted-foreground">Eigendom</dt>
                  <dd>{locatie.huur_koop ?? '-'}</dd>
                  <dt className="text-muted-foreground">Toegangsweg</dt>
                  <dd>{locatie.toegangsweg_breedte_m ? `${locatie.toegangsweg_breedte_m}m breed` : '-'}</dd>
                  <dt className="text-muted-foreground">Postcodegebied</dt>
                  <dd>{locatie.postcodegebied_2 ?? '-'}</dd>
                </dl>
                {locatie.spula_blokkade && (
                  <div className="mt-3 text-sm bg-amber-50 text-amber-800 rounded p-2">
                    SPULA blokkade: {locatie.spula_blokkade}
                  </div>
                )}
                {locatie.notities && (
                  <div className="mt-3 text-sm bg-gray-50 rounded p-2">
                    {locatie.notities}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Score overview in overzicht tab */}
            {autoScore && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    Score-analyse
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger>
                          <Info className="h-3.5 w-3.5 text-muted-foreground" />
                        </TooltipTrigger>
                        <TooltipContent>
                          Scores worden automatisch berekend op basis van locatie-eigenschappen en omgevingsdata. Hover over een score voor details.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <ScoreBar breakdown={autoScore.bereikbaarheid} icon={<Navigation className="h-3.5 w-3.5" />} />
                  <ScoreBar breakdown={autoScore.netcapaciteit} icon={<Zap className="h-3.5 w-3.5" />} />
                  <ScoreBar breakdown={autoScore.concurrentie} icon={<Shield className="h-3.5 w-3.5" />} />
                  <ScoreBar breakdown={autoScore.vraag} icon={<TrendingUp className="h-3.5 w-3.5" />} />
                  <ScoreBar breakdown={autoScore.kosten} icon={<DollarSign className="h-3.5 w-3.5" />} />
                  <div className="pt-2 border-t flex items-center justify-between">
                    <span className="font-semibold">Totaal</span>
                    <span className="text-xl font-bold text-emerald-600">{autoScore.totaal.toFixed(1)}/10</span>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Kaart */}
        <TabsContent value="kaart">
          <Card>
            <CardContent className="p-0">
              {mapSites.length > 1 ? (
                <div className="h-[500px]">
                  <UnifiedMapComponent
                    sites={mapSites}
                    getMarkerColor={getMarkerColor}
                    className="h-full w-full rounded-lg"
                    markerSize={10}
                  />
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                  <div className="text-center">
                    <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Geen coordinaten beschikbaar voor kaartweergave</p>
                    <p className="text-xs mt-1">Voeg latitude/longitude toe aan deze locatie, of sync concurrenten via OCM</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
          <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-emerald-600 inline-block" /> Locatie</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-900 inline-block" /> &ge;350 kW</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-red-600 inline-block" /> Truck HPC</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-orange-500 inline-block" /> Fast DC</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded-full bg-blue-500 inline-block" /> &lt;50 kW</span>
          </div>
        </TabsContent>

        {/* Scores detail */}
        <TabsContent value="scores">
          {autoScore ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { breakdown: autoScore.bereikbaarheid, icon: <Navigation className="h-4 w-4" /> },
                { breakdown: autoScore.netcapaciteit, icon: <Zap className="h-4 w-4" /> },
                { breakdown: autoScore.concurrentie, icon: <Shield className="h-4 w-4" /> },
                { breakdown: autoScore.vraag, icon: <TrendingUp className="h-4 w-4" /> },
                { breakdown: autoScore.kosten, icon: <DollarSign className="h-4 w-4" /> },
              ].map(({ breakdown, icon }) => (
                <Card key={breakdown.label}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span className="flex items-center gap-2">{icon} {breakdown.label}</span>
                      <span className="text-xl font-bold text-emerald-600">{breakdown.score}</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-2 bg-gray-200 rounded-full mb-3">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${breakdown.score * 10}%` }}
                      />
                    </div>
                    <div className="space-y-2">
                      {breakdown.factors.map((f, i) => (
                        <div key={i} className="text-xs border-l-2 pl-2 border-gray-200">
                          <div className="flex justify-between">
                            <span className="font-medium">{f.label}</span>
                            <span className={f.impact >= 0 ? 'text-green-600' : 'text-red-500'}>
                              {f.impact > 0 ? '+' : ''}{f.impact}
                            </span>
                          </div>
                          <div className="text-muted-foreground">{f.value}</div>
                          <div className="text-muted-foreground italic mt-0.5">{f.tooltip}</div>
                        </div>
                      ))}
                      {breakdown.factors.length === 0 && (
                        <p className="text-xs text-muted-foreground">Geen data beschikbaar voor scoring</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-muted-foreground">Score-berekening niet beschikbaar</p>
          )}
        </TabsContent>

        {/* Concurrenten */}
        <TabsContent value="concurrenten">
          {concurrenten.length > 0 ? (
            <div className="space-y-2">
              <div className="text-sm text-muted-foreground mb-2">
                {concurrenten.length} concurrenten gevonden in de omgeving
              </div>
              {concurrenten.map((c: any, i: number) => (
                <Card key={c.id || i}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium text-sm">{c.naam}</div>
                        <div className="text-xs text-muted-foreground">{c.adres}</div>
                        <div className="flex gap-2 mt-1">
                          {(c.aantal_truck_laders ?? 0) > 0 && (
                            <Badge variant="outline" className="text-xs">
                              <Truck className="h-3 w-3 mr-1" />
                              {c.aantal_truck_laders} truck
                            </Badge>
                          )}
                          {(c.aantal_auto_laders ?? 0) > 0 && (
                            <Badge variant="outline" className="text-xs">
                              {c.aantal_auto_laders} auto
                            </Badge>
                          )}
                          {c.max_vermogen_kw && (
                            <Badge variant="outline" className="text-xs">
                              {c.max_vermogen_kw} kW
                            </Badge>
                          )}
                          {c.operator && (
                            <span className="text-xs text-muted-foreground">{c.operator}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-emerald-700">
                          {c.afstand_km != null ? `${c.afstand_km} km` : '-'}
                        </div>
                        <div className={`text-xs ${c.operationeel ? 'text-green-600' : 'text-amber-600'}`}>
                          {c.operationeel ? 'Operationeel' : 'Niet operationeel'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Shield className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Geen concurrentiedata beschikbaar</p>
              <p className="text-xs mt-1">Sync concurrenten via de Concurrentie-tab in Laadplein Intelligence</p>
            </div>
          )}
        </TabsContent>

        {/* Verkeer */}
        <TabsContent value="verkeer">
          {verkeersdata.length > 0 ? (
            <div className="space-y-2">
              {verkeersdata.map((v: any, i: number) => (
                <Card key={v.id || i}>
                  <CardContent className="p-3">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-medium text-sm">{v.weg} — {v.richting}</div>
                        <div className="text-xs text-muted-foreground">
                          Meetpunt: {v.meetpunt ?? '-'} · Datum: {v.datum ?? '-'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-semibold">{v.vrachtwagens_per_dag?.toLocaleString() ?? '-'}</div>
                        <div className="text-xs text-muted-foreground">vrachtwagens/dag</div>
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Bron: {v.bron ?? '-'}
                      {v.personenautos_per_dag && ` · ${v.personenautos_per_dag.toLocaleString()} auto's/dag`}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <TrendingUp className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>Geen verkeersdata beschikbaar</p>
              <p className="text-xs mt-1">
                {nearestCorridor
                  ? `Dichtstbijzijnde corridor: ${nearestCorridor.weg} (${nearestCorridor.trucks_dag.toLocaleString()} trucks/dag, ${nearestCorridor.afstand_km} km)`
                  : 'Importeer verkeersdata via de Corridors-tab of NDW import'
                }
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
